import * as THREE from "three";
import type { DustLook } from "./styles";
import { blobTexture } from "./textures";

// Dust in the air (#179, realistic): a fixed set of motes drifting slowly
// around the camera, each wrapping to the far side of a box around the
// point the camera looks at when it falls out, so the air always has some
// in it wherever the rider goes. Render-only; it moves on the render clock.

/** How tall the box of motes is, from just above the ground at its center. */
export const DUST_HEIGHT = 4;

/** A small seeded generator, so a fresh set of motes always starts the same. */
function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** `v` moved by whole box widths into [c - r, c + r). */
export function wrap(v: number, c: number, r: number): number {
  const w = 2 * r;
  return v - Math.floor((v - (c - r)) / w) * w;
}

export class Dust {
  readonly points: THREE.Points;
  private readonly pos: Float32Array;
  /** Each mote's own drift, metres a second. */
  private readonly vel: Float32Array;
  /** Each mote's sway phase. */
  private readonly phase: Float32Array;
  private readonly mat: THREE.PointsMaterial;
  private t = 0;

  constructor(private readonly look: DustLook) {
    const n = look.count;
    this.pos = new Float32Array(n * 3);
    this.vel = new Float32Array(n * 3);
    this.phase = new Float32Array(n);
    const rand = rng(179);
    for (let i = 0; i < n; i++) {
      this.pos[i * 3] = (rand() * 2 - 1) * look.radius;
      this.pos[i * 3 + 1] = rand() * DUST_HEIGHT;
      this.pos[i * 3 + 2] = (rand() * 2 - 1) * look.radius;
      // A light breeze one way, a slow settle, and each its own wander.
      this.vel[i * 3] = 0.08 + (rand() - 0.5) * 0.1;
      this.vel[i * 3 + 1] = -0.02 + (rand() - 0.5) * 0.04;
      this.vel[i * 3 + 2] = 0.03 + (rand() - 0.5) * 0.1;
      this.phase[i] = rand() * Math.PI * 2;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    const tex = blobTexture();
    this.mat = new THREE.PointsMaterial({
      color: 0xfff1d6,
      size: look.size,
      sizeAttenuation: true,
      transparent: true,
      opacity: look.opacity,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      ...(tex ? { alphaMap: tex } : {}),
    });
    this.points = new THREE.Points(geo, this.mat);
    // The box moves with the camera; its bounding sphere never would.
    this.points.frustumCulled = false;
    this.points.renderOrder = 3;
  }

  /** The motes in the sunlight's color. */
  setColor(c: THREE.Color): void {
    this.mat.color.copy(c);
  }

  /** Drift `dt` seconds, kept in the box around (cx, cz), standing on `floor`. */
  update(dt: number, cx: number, floor: number, cz: number): void {
    this.t += dt;
    const r = this.look.radius;
    const p = this.pos;
    for (let i = 0; i < this.phase.length; i++) {
      const sway = Math.sin(this.t * 0.7 + this.phase[i]!) * 0.05;
      const j = i * 3;
      p[j] = wrap(p[j]! + (this.vel[j]! + sway) * dt, cx, r);
      p[j + 1] = wrap(p[j + 1]! + (this.vel[j + 1]! + sway * 0.5) * dt, floor + DUST_HEIGHT / 2, DUST_HEIGHT / 2);
      p[j + 2] = wrap(p[j + 2]! + (this.vel[j + 2]! - sway) * dt, cz, r);
    }
    (this.points.geometry.getAttribute("position") as THREE.BufferAttribute).needsUpdate = true;
  }

  /** Where mote `i` is. */
  at(i: number): [number, number, number] {
    return [this.pos[i * 3]!, this.pos[i * 3 + 1]!, this.pos[i * 3 + 2]!];
  }

  dispose(): void {
    this.points.geometry.dispose();
    this.mat.alphaMap?.dispose();
    this.mat.dispose();
  }
}
